import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { submitPayment } from "@/lib/payment";
import { useAuth } from "@/context/AuthContext";
import { AuthDialog } from "@/components/AuthDialog";
import { toast } from "sonner";
import { Loader2, Lock, Download, Play, Images, Mail, ShieldCheck, ChevronLeft, ChevronRight } from "lucide-react";

const euros = (c) => `${((c || 0) / 100).toFixed((c % 100) ? 2 : 0)} €`;

export default function MessageAccess() {
  const { code } = useParams();
  const { user, loading } = useAuth();
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [slide, setSlide] = useState(0);
  const polling = useRef(false);

  const load = async () => {
    try {
      const r = await api.get(`/living-access/message/${code}`);
      setInfo(r.data);
      if (r.data.unlocked && r.data.has_video) loadVideo();
    } catch (e) {
      setError(e.response?.status === 404 ? t("po.ac_err_invalid") : t("po.ac_err_load"));
    }
  };

  const loadVideo = async () => {
    try {
      const r = await api.get(`/living-access/message/${code}/video`, { responseType: "blob" });
      setVideoUrl((p) => { if (p) URL.revokeObjectURL(p); return URL.createObjectURL(r.data); });
    } catch { /* ignore */ }
  };

  useEffect(() => { if (!loading) load(); /* eslint-disable-next-line */ }, [loading, user, code]);

  useEffect(() => {
    const order = params.get("order");
    if (order && user && !polling.current) {
      polling.current = true;
      let n = 0;
      const poll = async () => {
        n += 1;
        try {
          const r = await api.get(`/living-pay/status/${order}`);
          if (r.data.status === "paid") { toast.success(t("po.ma_unlocked")); await load(); cleanup(); return; }
        } catch { /* keep */ }
        if (n >= 15) { cleanup(); return; }
        setTimeout(poll, 2000);
      };
      const cleanup = () => { const p = new URLSearchParams(params); p.delete("order"); p.delete("paid"); setParams(p, { replace: true }); polling.current = false; };
      poll();
    }
    if (params.get("canceled")) { toast.info(t("po.ac_pay_canceled")); const p = new URLSearchParams(params); p.delete("canceled"); setParams(p, { replace: true }); }
    // eslint-disable-next-line
  }, [params, user]);

  const unlock = async () => {
    if (!user) { setAuthOpen(true); return; }
    setBusy(true);
    try {
      const r = await api.post("/living-pay/checkout", {
        product_type: "message", ref: code,
        origin_url: window.location.origin, return_path: window.location.pathname,
      });
      if (["free", "simulated", "already"].includes(r.data.mode)) { toast.success(t("po.ma_unlocked")); await load(); }
      else submitPayment(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || t("po.ac_pay_fail")); }
    finally { setBusy(false); }
  };

  const download = () => {
    const token = localStorage.getItem("bdc_token");
    fetch(`${api.defaults.baseURL}/living-access/message/${code}/download`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
      .then((b) => { const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href = u; a.download = "message.zip"; a.click(); URL.revokeObjectURL(u); })
      .catch(() => toast.error(t("po.ac_dl_fail")));
  };

  if (error) return <Shell><div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-6 text-center text-sm text-red-300" data-testid="message-access-error">{error}</div></Shell>;
  if (!info) return <Shell><div className="flex justify-center py-16"><Loader2 className="animate-spin text-amber-300" /></div></Shell>;

  return (
    <Shell>
      <div data-testid="message-access-page">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-amber-400/30 px-3 py-1 text-xs text-amber-200"><Mail size={13} /> {t("po.ma_badge")}</div>
        <h1 className="text-2xl font-semibold text-foreground">{info.title || t("po.ma_default_title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("po.ma_from")} {info.owner_name || t("po.ma_someone")}{info.recipient_name ? ` · ${t("po.ma_for")} ${info.recipient_name}` : ""}</p>

        {info.unlocked ? (
          <div className="mt-6 space-y-5" data-testid="message-unlocked">
            {info.text && <p className="whitespace-pre-wrap rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-sm leading-relaxed text-foreground/90">{info.text}</p>}
            {(info.photos?.length > 0) && (
              <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-black">
                <img src={info.photos[slide]} alt="" className="max-h-[420px] w-full object-contain" />
                {info.photos.length > 1 && (<>
                  <button onClick={() => setSlide((s) => (s - 1 + info.photos.length) % info.photos.length)} className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-1.5 text-white"><ChevronLeft size={18} /></button>
                  <button onClick={() => setSlide((s) => (s + 1) % info.photos.length)} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-1.5 text-white"><ChevronRight size={18} /></button>
                </>)}
              </div>
            )}
            {info.has_video && (videoUrl ? <video src={videoUrl} controls className="max-h-[420px] w-full rounded-2xl bg-black" /> : <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 size={14} className="animate-spin" /> {t("po.ac_loading_video")}</div>)}
            {info.access_type === "download" && (
              <Button className="rounded-full" onClick={download} data-testid="message-download-btn"><Download size={15} className="mr-2" /> {t("po.ma_download_btn")}</Button>
            )}
          </div>
        ) : (
          <div className="mt-6 rounded-3xl border border-amber-400/20 bg-amber-500/[0.05] p-6 text-center" data-testid="message-locked">
            <Lock className="mx-auto mb-3 text-amber-300" size={30} />
            <div className="mb-3 flex justify-center gap-4 text-sm text-muted-foreground">
              {info.photos_count > 0 && <span className="inline-flex items-center gap-1"><Images size={14} /> {t("po.ac_photos_n", { count: info.photos_count })}</span>}
              {info.has_video && <span className="inline-flex items-center gap-1"><Play size={14} /> {t("po.ac_video")}</span>}
            </div>
            {!user ? (
              <>
                <p className="text-sm text-muted-foreground">{t("po.ma_create_msg", { action: info.free ? t("po.ma_read") : t("po.ma_unlock_word") })}</p>
                <Button className="mt-4 w-full rounded-full bg-amber-500 py-3 font-semibold text-black hover:bg-amber-400" onClick={() => setAuthOpen(true)} data-testid="message-account-btn"><ShieldCheck size={16} className="mr-2" /> {t("po.ac_account_btn")}</Button>
              </>
            ) : info.free ? (
              <Button className="mt-1 w-full rounded-full bg-amber-500 py-3 font-semibold text-black hover:bg-amber-400" onClick={unlock} disabled={busy} data-testid="message-unlock-btn">{busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : null} {t("po.ma_read_free_btn")}</Button>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">{t("po.ma_unlock_msg", { price: euros(info.price_cents) })}</p>
                <Button className="mt-4 w-full rounded-full bg-amber-500 py-3 font-semibold text-black hover:bg-amber-400" onClick={unlock} disabled={busy} data-testid="message-unlock-btn">{busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Lock size={16} className="mr-2" />} {t("po.ac_unlock_price_btn", { price: euros(info.price_cents) })}</Button>
              </>
            )}
          </div>
        )}
      </div>
      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} initialMode="register" onSuccess={() => { setAuthOpen(false); load(); }} />
    </Shell>
  );
}

function Shell({ children }) {
  return <div className="min-h-screen bg-black"><Header /><div className="mx-auto max-w-lg px-5 py-10">{children}</div></div>;
}
